import { useState, useEffect } from "react";
import { X } from "lucide-react";

interface AllergyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (allergy: AllergyData) => void;
  editData?: AllergyData | null;
  onDelete?: (id: number) => void;
  loading?: boolean;
}

export interface AllergyData {
  id?: number;
  type: "non-drug" | "drug" | "no-known";
  name: string;
  reaction: string;
  severity: "mild" | "moderate" | "severe" | "fatal" | "";
  note: string;
}

const SeverityIcon = ({ severity }: { severity: "mild" | "moderate" | "severe" | "fatal" }) => {
  const colors = {
    mild: "#22c55e",
    moderate: "#f97316",
    severe: "#ec4899",
    fatal: "#000000",
  };

  const color = colors[severity];

  return (
    <svg width="64" height="64" viewBox="0 0 64 64">
      <defs>
        <pattern id={`dots-${severity}`} x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
          <circle cx="4" cy="4" r="2" fill={color} />
        </pattern>
      </defs>
      <circle cx="32" cy="32" r="32" fill={`url(#dots-${severity})`} />
    </svg>
  );
};

export default function AllergyModal({
  isOpen,
  onClose,
  onSave,
  editData,
  onDelete,
  loading = false,
}: AllergyModalProps) {
  const [allergyType, setAllergyType] = useState<"non-drug" | "drug" | "no-known">("non-drug");
  const [name, setName] = useState("");
  const [reaction, setReaction] = useState("");
  const [severity, setSeverity] = useState<"mild" | "moderate" | "severe" | "fatal" | "">("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (editData) {
      setAllergyType(editData.type);
      setName(editData.name);
      setReaction(editData.reaction);
      setSeverity(editData.severity);
      setNote(editData.note);
    } else {
      setAllergyType("non-drug");
      setName("");
      setReaction("");
      setSeverity("");
      setNote("");
    }
  }, [editData, isOpen]);

  const handleSave = () => {
    if (allergyType === "no-known") {
      onSave({
        id: editData?.id,
        type: allergyType,
        name: "No known allergies",
        reaction: "",
        severity: "",
        note,
      });
    } else {
      onSave({
        id: editData?.id,
        type: allergyType,
        name,
        reaction,
        severity,
        note,
      });
    }
    handleClose();
  };

  const handleDelete = () => {
    if (editData?.id && onDelete) {
      onDelete(editData.id);
      handleClose();
    }
  };

  const handleClose = () => {
    setAllergyType("non-drug");
    setName("");
    setReaction("");
    setSeverity("");
    setNote("");
    onClose();
  };

  if (!isOpen) return null;

  const isNoKnownAllergies = allergyType === "no-known";

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/30"
        onClick={handleClose}
      />
      <div className="relative bg-white rounded-l-2xl rounded-r-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">
            {editData ? "Edit allergy" : "Add allergy"}
          </h2>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-6">
          {/* Type Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setAllergyType("non-drug")}
              className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition ${allergyType === "non-drug"
                  ? "bg-gray-100 text-gray-900"
                  : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
            >
              Non-drug allergy
            </button>
            <button
              onClick={() => setAllergyType("drug")}
              className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition ${allergyType === "drug"
                  ? "bg-gray-100 text-gray-900"
                  : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
            >
              Drug allergy
            </button>
            <button
              onClick={() => setAllergyType("no-known")}
              className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition ${allergyType === "no-known"
                  ? "bg-gray-100 text-gray-900"
                  : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
            >
              No known allergies
            </button>
          </div>

          {!isNoKnownAllergies && (
            <>
              {/* Name and Reaction */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter allergy name"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Reaction
                  </label>
                  <select
                    value={reaction}
                    onChange={(e) => setReaction(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none bg-white"
                  >
                    <option value="">Select an option</option>
                    <option value="Acute kidney failure">Acute kidney failure</option>
                    <option value="Anaphylaxis">Anaphylaxis</option>
                    <option value="Rash">Rash</option>
                    <option value="Hives">Hives</option>
                    <option value="Swelling">Swelling</option>
                  </select>
                </div>
              </div>

              {/* Severity */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-900">
                    Severity
                  </label>
                  <span className="text-xs text-gray-500">
                    Leave empty if severity unknown
                  </span>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  {(["mild", "moderate", "severe", "fatal"] as const).map((sev) => (
                    <button
                      key={sev}
                      onClick={() => setSeverity(sev)}
                      className={`flex flex-col items-center gap-3 p-4 rounded-lg border-2 transition ${severity === sev
                          ? "border-purple-500 bg-purple-50"
                          : "border-gray-200 hover:border-purple-300 hover:bg-purple-50"
                        }`}
                    >
                      <SeverityIcon severity={sev} />
                      <span className="text-sm font-medium text-gray-900 capitalize">
                        {sev}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Note */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-900">Note</label>
              <span className="text-xs text-gray-500">{note.length}/1000</span>
            </div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 1000))}
              placeholder="Enter allergy notes here"
              rows={4}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3 flex-shrink-0">
          {editData && (
            <button
              onClick={handleDelete}
              className="px-5 py-2.5 text-red-600 font-medium rounded-lg hover:bg-red-50 transition"
            >
              Delete
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={loading}
            className={`px-8 py-2.5 font-medium rounded-lg transition ${loading
                ? "bg-gray-400 text-white cursor-not-allowed"
                : "bg-[#885ABB] text-white hover:bg-[#6f47a2]"
              }`}
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
